import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import { getSession, logout as apiLogout, type DashboardUser } from '../api/auth';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionToken: string | null;
  user: DashboardUser | null;
}

interface AuthContextValue extends AuthState {
  login: (token: string, user: DashboardUser) => void;
  logout: () => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const STORAGE_KEY = 'niftee_session';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    sessionToken: null,
    user: null,
  });

  // Validate existing session on mount
  useEffect(() => {
    async function validateSession() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
          setState((s) => ({ ...s, isLoading: false }));
          return;
        }

        const { token } = JSON.parse(stored);
        if (!token) {
          localStorage.removeItem(STORAGE_KEY);
          setState((s) => ({ ...s, isLoading: false }));
          return;
        }

        // Validate with backend
        const response = await getSession(token);
        setState({
          isAuthenticated: true,
          isLoading: false,
          sessionToken: token,
          user: response.user,
        });
      } catch {
        // Session invalid or expired
        localStorage.removeItem(STORAGE_KEY);
        setState({
          isAuthenticated: false,
          isLoading: false,
          sessionToken: null,
          user: null,
        });
      }
    }

    validateSession();
  }, []);

  const login = useCallback((token: string, user: DashboardUser) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ token }));
    setState({
      isAuthenticated: true,
      isLoading: false,
      sessionToken: token,
      user,
    });
  }, []);

  const logout = useCallback(async () => {
    const token = state.sessionToken;
    if (token) {
      try {
        await apiLogout(token);
      } catch {
        // Ignore logout errors
      }
    }
    localStorage.removeItem(STORAGE_KEY);
    setState({
      isAuthenticated: false,
      isLoading: false,
      sessionToken: null,
      user: null,
    });
  }, [state.sessionToken]);

  const isAdmin = state.user?.role === 'admin';

  return (
    <AuthContext.Provider value={{ ...state, login, logout, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
